/*
 * SoMatcha charm dialog — the PRODUCT PAGE variant.
 *
 * Deliberately separate from assets/sm-charm-popup.js, which serves product cards. That
 * file binds through closest('.sm-card') and drives its own submissions; this one has no
 * card to bind to and does not touch the form at all. Keeping them apart means
 * sm-charm-popup.js and snippets/card-product.liquid stay byte-identical, so the shipped
 * and tested card flow cannot regress from this work.
 *
 * This element owns PRESENTATION ONLY. It never reads or mutates the product form and
 * never submits anything. assets/sm-tumbler-addon.js owns the form, the capture-phase
 * handler and the cart integration, and drives this dialog through open() / close() /
 * showMessage(). The dialog reports the customer's decision back with a single
 * `sm-charm:choice` event carrying { withCharm }.
 *
 * Every exit reports a choice, so every exit results in the product being added:
 *   "+ Add"                 -> choice(true)   product + charm
 *   "Add product only"      -> choice(false)  product only
 *   X / Escape / backdrop   -> choice(false)  product only — declining the charm is never
 *                                             cancelling the purchase
 *
 * Extends the theme's ModalDialog (assets/global.js:602), which is NOT modified: focus
 * trap, Escape, backdrop click, focus return to the opener and body scroll lock all come
 * from there.
 */
if (typeof ModalDialog !== 'undefined' && !customElements.get('sm-charm-dialog')) {
  class SmCharmDialog extends ModalDialog {
    constructor() {
      // ModalDialog's constructor dereferences [id^="ModalClose-"] with no null guard,
      // so the markup must always carry that button. It does.
      super();
      this.choiceMade = false;
    }

    connectedCallback() {
      // Relocates to document.body on the first call and re-enters; the base class guards
      // with its own `moved` flag and `wired` keeps this wiring to exactly one run.
      super.connectedCallback();
      if (this.wired) return;
      this.wired = true;

      this.addButton = this.querySelector('.sm-charm-popup__add');
      this.addOnlyButton = this.querySelector('.sm-charm-popup__add-only');
      this.messageEl = this.querySelector('.sm-charm-popup__message');

      if (this.addButton) this.addButton.addEventListener('click', () => this.choose(true));
      if (this.addOnlyButton) this.addOnlyButton.addEventListener('click', () => this.choose(false));
    }

    /* Called by sm-tumbler-addon.js. `opener` is the Add to Cart button, so ModalDialog
       returns focus to it on close. */
    open(opener) {
      this.choiceMade = false;
      this.hideMessage();
      this.show(opener);
    }

    /*
     * Exactly one choice per opening. choiceMade is set here and reset only in open(), so
     * a second press of any control while the request is in flight is ignored rather than
     * reported twice — the dialog can never ask the owner to add the product more than
     * once for a single opening.
     */
    choose(withCharm) {
      if (this.choiceMade) return;
      this.choiceMade = true;
      this.dispatchEvent(
        new CustomEvent('sm-charm:choice', { bubbles: true, detail: { withCharm: withCharm } })
      );
    }

    /*
     * ModalDialog funnels the close button, Escape and backdrop clicks here. Before a
     * choice has been made, all three mean "no charm, but still buy the product", so they
     * report choice(false) and leave the dialog open — the owner closes it on success,
     * which keeps it available for the sold-out-charm message. Once a choice exists this
     * is an ordinary close.
     */
    hide() {
      if (!this.choiceMade) {
        this.choose(false);
        return;
      }
      super.hide();
    }

    /* Programmatic close by the owner, after the add succeeds. Bypasses the hide()
       override so it can never report a further choice. */
    close() {
      this.choiceMade = true;
      super.hide();
    }

    showMessage(text) {
      if (!this.messageEl || !text) return;
      this.messageEl.textContent = text;
      this.messageEl.removeAttribute('hidden');
    }

    hideMessage() {
      if (!this.messageEl) return;
      this.messageEl.textContent = '';
      this.messageEl.setAttribute('hidden', '');
    }
  }

  customElements.define('sm-charm-dialog', SmCharmDialog);
}
